// components/LeftPanel.js
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { formatDate, formatDay } from "utils/helpers";
import FileView from "components/fileView";
import { LoadingIcon } from "utils/constant";
import ShowUI, { CanShow, LEGISLATIVE_DRAFTING_UI_RULES } from "utils/authRules";
import { updateLegislativeDrafting } from "services/legislativeService";
import { useToast } from "contexts/ToastContext";
import { LEGISLATIVE_DRAFTING_REQUEST_STATUS } from "utils/constantValues";
import HeadRemark from "./headRemark";
import DirectorRemark from "./directorRemark";
import CoordinatorRemark from "./coordinatorRemark";
import ProsecutorRemark from "./prosecutorRemark";
import ActionAvatars from "pages/conductLegalResearch/fragments/actionAvatars";
import PublicFeedback from "./publicFeedback";
import UploadAmendedFile from "./uploadAmendedFile";
import DynamicFormInput from "components/form/form";
import { useAuth } from "contexts/AuthContext";
import { StatusChip } from "components/StatusChip";
import RespondBackForm from "pages/conductLegalResearch/fragments/respondBackform";
import LegislativeRespondBackForm from "./LegislativeResponback";
import TeamView from "components/team_view";
import EditTeamModal from "./editTeamModal";
import DocumentViewerModal from "components/DocumentViewer";
import ConfirmationModal1 from "components/ConfirmationModal1";
import BroadcastCard from "pages/conductLegalResearch/fragments/broadcastCard";
import { useTranslation } from "react-i18next";

function LegislativeDraftDetail({ request }: { request: any }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [publicFeedback, setPublicFeedback] = useState(false);
    const [uploadAmend, setUploadAmend] = useState(false);
    const [auditDraft, setAuditDraft] = useState(false);
    const [showDraftHistory, setShowDraftHistory] = useState(false);
    const [showAuditHistory, setShowAuditHistory] = useState(false);
    const [showPreviousDrafts, setShowPreviousDrafts] = useState(false);
    const [showPreviousAmended, setShowPreviousAmended] = useState(false);
    const [showPreviousFeedback, setShowPreviousFeedback] = useState(false);
    const [edit, setEdit] = useState(false);
    const [editDraftDetail, setEditDraftDetail] = useState(false);
    const [draftDetailLoading, setDraftDetailLoading] = useState(false);
    const [newDraftFile, setNewDraftFile] = useState<any>(null);
    const [viewTeam, setViewTeam] = useState(false);
    const [isEditTeamModalOpen, setIsEditTeamModalOpen] = useState(false);
    const [formState, setFormState] = useState({ ...request });
    const contextData = useAuth();

    // API base URL for additional info files
    const ADDITIONAL_INFO_BASE_URL = "https://research-api.oagb.et/api/legislative_drafting/additional-info-files/document";

    // Pick the most recent additional-info entry so only the latest
    // request/response (and its response file) is shown.
    const latestAdditionalInfo = React.useMemo(() => {
        const infos = formState?.additional_infos ?? [];
        if (infos.length === 0) return null;
        return [...infos].sort((a: any, b: any) => {
            const da = new Date(a?.responded_at || a?.requested_at || 0).getTime();
            const db = new Date(b?.responded_at || b?.requested_at || 0).getTime();
            return db - da;
        })[0];
    }, [formState]);

    const additionalInfoDocuments = React.useMemo(() => {
        const info = latestAdditionalInfo;
        if (!info) return [];

        const uploadedFiles = info?.uploaded_files || [];

        return uploadedFiles.map((file: any) => ({
            uu_id: file?.uu_id || `${formState.id}-${file?.document_type}`,
            document_type: file?.document_type || "request_file",
            document_uuid: file?.document_uuid,
            document_path: file?.document_path || "",
            created_at: file?.created_at || info?.requested_at || formState?.created_at || new Date().toISOString(),
            updated_at: file?.updated_at || info?.responded_at || info?.requested_at || formState?.updated_at || new Date().toISOString(),
        }));
    }, [formState]);

    const latestDraft = React.useMemo(() => {
        const lawDraftFiles = formState?.uploaded_files?.filter((document: any) => document.document_type === "law_draft") || [];
        const sortedDraftFiles = [...lawDraftFiles].sort((a: any, b: any) => new Date(b.created_at || b.updated_at).getTime() - new Date(a.created_at || a.updated_at).getTime());
        return sortedDraftFiles[0];
    }, [formState.uploaded_files]);

    const previousDrafts = React.useMemo(() => {
        const lawDraftFiles = formState?.uploaded_files?.filter((document: any) => document.document_type === "law_draft") || [];
        const sortedDraftFiles = [...lawDraftFiles].sort((a: any, b: any) => new Date(b.created_at || b.updated_at).getTime() - new Date(a.created_at || a.updated_at).getTime());
        return sortedDraftFiles.slice(1);
    }, [formState.uploaded_files]);

    const { showToast } = useToast();

    const [isRespondBackFormOpen, setIsRespondBackFormOpen] = useState(false);
    const [showConfirmPublish, setShowConfirmPublish] = useState(false);
    const [broadcastLoading, setBroadcastLoading] = useState(false);

    const handleFileChange = (
        file: any,
        fieldName: keyof Pick<
            typeof formState,
            "audit_summary_file"
        >
    ) => {
        setFormState((prev: any) => ({
            ...prev,
            [fieldName]: file,
        }));
    };

    const handleChange = (
        e: React.ChangeEvent<
            HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
        >
    ) => {
        const { name, value } = e.target;
        setFormState((prev: any) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleRespondBackClick = () => {
        setIsRespondBackFormOpen(!isRespondBackFormOpen);
    }

    const handleAdditionalInfoApprove = async () => {
        setLoading(true);
        const data = new FormData();

        data.append("forward_status", LEGISLATIVE_DRAFTING_REQUEST_STATUS.forwarded_to_registrar.value);

        try {
            const response = await updateLegislativeDrafting(formState.id, data);

            setFormState((prev: any) => ({ ...prev, ...response }));
            showToast(t("ProposalApprovedSuccess") || "Legal research proposal Approved!", "success");
        } catch (error) {
            console.error("Error submitting request:", error);
            showToast(t("FailedToUpdateApproval") || "Failed to update legal research proposal Approval.", "error");
        } finally {
            setLoading(false);
        }
    }

    const handleReject = () => {
    }

    const handleForward = async (froward_status: string) => {
        const data = new FormData();
        if (formState.current_status === "Under review" && froward_status === LEGISLATIVE_DRAFTING_REQUEST_STATUS.forwarded_to_head.value) {
            showToast(t("CannotForwardBeforeEditing") || "Can not forward before editing.", "error");
            return;
        }
        if (formState.current_status === "Back to Head" && froward_status === LEGISLATIVE_DRAFTING_REQUEST_STATUS.forwarded_to_director.value) {
            showToast(t("CannotForwardBeforeEditing") || "Can not forward before editing.", "error");
            return;
        }

        if (formState.current_status === "Back to Director" && froward_status === LEGISLATIVE_DRAFTING_REQUEST_STATUS.forwarded_to_coordinator.value) {
            showToast(t("CannotForwardBeforeEditing") || "Can not forward before editing.", "error");
            return;
        }

        data.append(
            "forward_status",
            froward_status
        );

        try {
            const response = await updateLegislativeDrafting(formState.id, data);

            setFormState((prev: any) => ({ ...prev, ...response }));
            showToast(t("LegislativeDraftForwardedSuccess") || "Legislative Draft Forwarded successfully!", "success");
        } catch (error) {
            console.error("Error submitting request:", error);
            showToast(t("FailedToUpdateLegislativeDraftRequest") || "Failed to update Legislative Draft request.", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleAudit = async () => {
        const data = new FormData();
        data.append("audit_summary", formState.audit_summary);
        data.append("audit_summary_file", formState.audit_summary_file);
        data.append("current_status", LEGISLATIVE_DRAFTING_REQUEST_STATUS.audit_submitted.value);

        try {
            const response = await updateLegislativeDrafting(formState.id, data);

            setFormState((prev: any) => ({ ...prev, ...response }));
            showToast(t("LegislativeDraftForwardedSuccess") || "Legislative Draft Forwarded successfully!", "success");
        } catch (error) {
            console.error("Error submitting request:", error);
            showToast(t("FailedToUpdateLegislativeDraftRequest") || "Failed to update Legislative Draft request.", "error");
        } finally {
            setLoading(false);
            setAuditDraft(false);
        }
    };

    const handleDraftAllowFeedback = async () => {
        const data = new FormData();
        data.append("current_status", LEGISLATIVE_DRAFTING_REQUEST_STATUS.public_feedback.value);
        data.append("forward_status", LEGISLATIVE_DRAFTING_REQUEST_STATUS.forwarded_to_coordinator.value);
        data.append("public_feedback_allowed", "true");

        try {
            const response = await updateLegislativeDrafting(formState.id, data);

            setFormState((prev: any) => ({ ...prev, ...response }));
            showToast(t("LegislativeDraftForwardedSuccess") || "Legislative Draft Forwarded successfully!", "success");
        } catch (error) {
            console.error("Error submitting request:", error);
            showToast(t("FailedToUpdateLegislativeDraftRequest") || "Failed to update Legislative Draft request.", "error");
        } finally {
            setLoading(false);
            setAuditDraft(false);
        }
    };

    const handlePublish = async () => {
        const data = new FormData();
        data.append("current_status", LEGISLATIVE_DRAFTING_REQUEST_STATUS.published.value);
        data.append("forward_status", LEGISLATIVE_DRAFTING_REQUEST_STATUS.not_forwarded.value);
        data.append("is_published", "true");

        try {
            const response = await updateLegislativeDrafting(formState.id, data);

            setFormState((prev: any) => ({ ...prev, ...response }));
            showToast(t("LegislativeDraftForwardedSuccess") || "Legislative Draft Forwarded successfully!", "success");
        } catch (error) {
            console.error("Error submitting request:", error);
            showToast(t("FailedToUpdateLegislativeDraftRequest") || "Failed to update Legislative Draft request.", "error");
        } finally {
            setLoading(false);
            setAuditDraft(false);
            setShowConfirmPublish(false);
        }
    };

    const handleBroadcast = async () => {
        setBroadcastLoading(true);
        const data = new FormData();
        const newPublishedState = !formState.is_published;

        if (newPublishedState) {
            data.append("current_status", LEGISLATIVE_DRAFTING_REQUEST_STATUS.published.value);
            data.append("forward_status", LEGISLATIVE_DRAFTING_REQUEST_STATUS.not_forwarded.value);
            data.append("is_published", "true");
        } else {
            data.append("current_status", "draft_submitted");
            data.append("is_published", "false");
        }

        try {
            const response = await updateLegislativeDrafting(formState.id, data);
            setFormState((prev: any) => ({ ...prev, ...response, is_published: newPublishedState }));
            showToast(newPublishedState ? (t("LegislativeDraftPublishedSuccess") || "Legislative Draft published successfully!") : (t("LegislativeDraftUnpublishedSuccess") || "Legislative Draft unpublished successfully!"), "success");
        } catch (error) {
            console.error("Error updating broadcast status:", error);
            showToast(t("FailedToUpdateBroadcastStatus") || "Failed to update broadcast status.", "error");
        } finally {
            setBroadcastLoading(false);
        }
    };

    const handleDraftApprove = async () => {
        const data = new FormData();
        data.append("current_status", LEGISLATIVE_DRAFTING_REQUEST_STATUS.draft_approved.value);
        data.append("forward_status", LEGISLATIVE_DRAFTING_REQUEST_STATUS.forwarded_to_registrar.value);

        try {
            const response = await updateLegislativeDrafting(formState.id, data);

            setFormState((prev: any) => ({ ...prev, ...response }));
            showToast(t("LegislativeDraftForwardedSuccess") || "Legislative Draft Forwarded successfully!", "success");
        } catch (error) {
            console.error("Error submitting request:", error);
            showToast(t("FailedToUpdateLegislativeDraftRequest") || "Failed to update Legislative Draft request.", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleFinalApprove = async () => {
        const data = new FormData();
        data.append("current_status", LEGISLATIVE_DRAFTING_REQUEST_STATUS.final_approval.value);
        data.append("forward_status", LEGISLATIVE_DRAFTING_REQUEST_STATUS.forwarded_to_registrar.value);

        try {
            const response = await updateLegislativeDrafting(formState.id, data);

            setFormState((prev: any) => ({ ...prev, ...response }));
            showToast(t("LegislativeDraftForwardedSuccess") || "Legislative Draft Forwarded successfully!", "success");
        } catch (error) {
            console.error("Error submitting request:", error);
            showToast(t("FailedToUpdateLegislativeDraftRequest") || "Failed to update Legislative Draft request.", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleSaveDraftDetail = async () => {
        setDraftDetailLoading(true);
        const data = new FormData();
        data.append("draft_summary", formState.draft_summary || "");
        data.append("request_reference_number", formState.request_reference_number || "");

        if (newDraftFile) {
            data.append("draft_law_file", newDraftFile);
        }

        try {
            const response = await updateLegislativeDrafting(formState.id, data);
            setFormState((prev: any) => ({ ...prev, ...response }));
            showToast(t("DraftLawDetailUpdated") || "Draft Law Detail updated successfully!", "success");
            setEditDraftDetail(false);
            setNewDraftFile(null);
        } catch (error) {
            console.error("Error updating draft detail:", error);
            showToast(t("FailedToUpdateDraftLawDetail") || "Failed to update Draft Law Detail.", "error");
        } finally {
            setDraftDetailLoading(false);
        }
    };

    useEffect(() => { console.log("formState: ", formState) }, [formState]);

    // Draft review (edit the draft, respond back, approve and forward): the
    // coordinator once the prosecutor has forwarded the draft, and the director
    // too, who can act in the coordinator's place at that stage.
    const isDirector = !!contextData?.user?.roles?.includes("LRDIFUDDirector");
    const DRAFT_REVIEW_STATUSES = ["draft_submitted", "Draft Prepared", "proposal_submitted", "audit_completed"];
    const canReviewDraft =
        CanShow(LEGISLATIVE_DRAFTING_UI_RULES.forward_darft_to_director.value, formState.current_status, formState.forward_status) ||
        (isDirector &&
            formState.forward_status === LEGISLATIVE_DRAFTING_REQUEST_STATUS.forwarded_to_coordinator.value &&
            DRAFT_REVIEW_STATUSES.includes(formState.current_status));

    const getApproveButtonText = () => {
        if (formState.current_status === LEGISLATIVE_DRAFTING_REQUEST_STATUS.draft_submitted.value) {
            return t("ApproveDraftLaw") || "Approve Draft Law";
        }
        return t("Approve") || "Approve";
    };

    return (
        <div className="flex-1 bg-gray-100 mx-4 rounded-lg overflow-y-scroll scrollbar-hide">
            <div className="flex flex-col max-w-4xl bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                {/* Header */}
                <div className="flex items-center justify-between pb-4 mb-3">
                    <div className="flex items-center space-x-2">
                        <button
                            onClick={() => navigate("/legislative-drafting")}
                            className="text-gray-600 hover:text-gray-800"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-6 w-6"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M15 19l-7-7 7-7"
                                />
                            </svg>
                        </button>
                    </div>
                    <h1 className="text-2xl font-semibold text-gray-800 ml-8">
                        {t("RequestForLegislativeDrafting") || "Request for Legislative Drafting"}
                    </h1>
                    <div className="ml-auto">
                        <StatusChip status={formState.current_status} />
                        <StatusChip status={formState.forward_status} />
                    </div>
                    <ShowUI
                        ui_key={LEGISLATIVE_DRAFTING_UI_RULES.registrar_forward_to_coordinator.value}
                        currentStatus={formState.current_status}
                        forwardStatus={formState.forward_status}
                    >
                        <div className="ml-auto flex gap-4">
                            <button onClick={() => handleForward(LEGISLATIVE_DRAFTING_REQUEST_STATUS.forwarded_to_coordinator.value)} type="button" className="bg-[#212121] text-white rounded-lg px-4">{t("Forward") || "Forward"}</button>
                        </div>
                    </ShowUI>
                    <ShowUI
                        ui_key={LEGISLATIVE_DRAFTING_UI_RULES.stakeholder_forward_to_head.value}
                        currentStatus={formState.current_status}
                        forwardStatus={formState.forward_status}
                    >
                        <div className="flex ml-auto gap-4">
                            <div onClick={() => navigate("/legislative-drafting/request", { state: { request: formState } })} className="px-2 hover:bg-gray-100 rounded-lg cursor-pointer">
                                <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M3.66406 21V16.75L16.8641 3.575C17.0641 3.39167 17.2851 3.25 17.5271 3.15C17.7691 3.05 18.0231 3 18.2891 3C18.5551 3 18.8134 3.05 19.0641 3.15C19.3147 3.25 19.5314 3.4 19.7141 3.6L21.0891 5C21.2891 5.18333 21.4351 5.4 21.5271 5.65C21.6191 5.9 21.6647 6.15 21.6641 6.4C21.6641 6.66667 21.6184 6.921 21.5271 7.163C21.4357 7.405 21.2897 7.62567 21.0891 7.825L7.91406 21H3.66406ZM18.2641 7.8L19.6641 6.4L18.2641 5L16.8641 6.4L18.2641 7.8Z" fill="black" />
                                </svg>
                            </div>
                            <div className="flex gap-4">
                                <button onClick={() => handleForward(LEGISLATIVE_DRAFTING_REQUEST_STATUS.forwarded_to_head.value)} type="button" className="bg-[#212121] text-white rounded-lg px-4">{t("Forward") || "Forward"}</button>
                            </div>
                        </div>
                    </ShowUI>
                    <ShowUI
                        ui_key={LEGISLATIVE_DRAFTING_UI_RULES.head_forward_to_director.value}
                        currentStatus={formState.current_status}
                        forwardStatus={formState.forward_status}
                    >
                        <div className="flex ml-auto gap-4">
                            <div onClick={() => setEdit(!edit)} className="px-2 hover:bg-gray-100 rounded-lg cursor-pointer">
                                {edit ?
                                    <p>{t("Cancel") || "Cancel"}</p> :
                                    <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M3.66406 21V16.75L16.8641 3.575C17.0641 3.39167 17.2851 3.25 17.5271 3.15C17.7691 3.05 18.0231 3 18.2891 3C18.5551 3 18.8134 3.05 19.0641 3.15C19.3147 3.25 19.5314 3.4 19.7141 3.6L21.0891 5C21.2891 5.18333 21.4351 5.4 21.5271 5.65C21.6191 5.9 21.6647 6.15 21.6641 6.4C21.6641 6.66667 21.6184 6.921 21.5271 7.163C21.4357 7.405 21.2897 7.62567 21.0891 7.825L7.91406 21H3.66406ZM18.2641 7.8L19.6641 6.4L18.2641 5L16.8641 6.4L18.2641 7.8Z" fill="black" />
                                    </svg>}
                            </div>
                            <div className="flex gap-4">
                                <button onClick={() => handleForward(
                                    formState.current_status === "Under review" ? LEGISLATIVE_DRAFTING_REQUEST_STATUS.not_forwarded.value :
                                        LEGISLATIVE_DRAFTING_REQUEST_STATUS.forwarded_to_director.value)} type="button" className="bg-[#212121] text-white rounded-lg px-4">{t("Forward") || "Forward"}</button>
                            </div>
                        </div>
                    </ShowUI>
                    <ShowUI
                        ui_key={LEGISLATIVE_DRAFTING_UI_RULES.director_forward_to_coordinator.value}
                        currentStatus={formState.current_status}
                        forwardStatus={formState.forward_status}
                    >
                        <div className="flex ml-auto gap-4">
                            <div onClick={() => setEdit(!edit)} className="px-2 hover:bg-gray-100 rounded-lg cursor-pointer">
                                {edit ?
                                    <p>{t("Cancel") || "Cancel"}</p> :
                                    <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M3.66406 21V16.75L16.8641 3.575C17.0641 3.39167 17.2851 3.25 17.5271 3.15C17.7691 3.05 18.0231 3 18.2891 3C18.5551 3 18.8134 3.05 19.0641 3.15C19.3147 3.25 19.5314 3.4 19.7141 3.6L21.0891 5C21.2891 5.18333 21.4351 5.4 21.5271 5.65C21.6191 5.9 21.6647 6.15 21.6641 6.4C21.6641 6.66667 21.6184 6.921 21.5271 7.163C21.4357 7.405 21.2897 7.62567 21.0891 7.825L7.91406 21H3.66406ZM18.2641 7.8L19.6641 6.4L18.2641 5L16.8641 6.4L18.2641 7.8Z" fill="black" />
                                    </svg>}
                            </div>
                            <div className="flex gap-4">
                                <button onClick={() => handleForward(
                                    formState.current_status === LEGISLATIVE_DRAFTING_REQUEST_STATUS.back_to_head.value ? LEGISLATIVE_DRAFTING_REQUEST_STATUS.forwarded_to_head.value :
                                        LEGISLATIVE_DRAFTING_REQUEST_STATUS.forwarded_to_coordinator.value)} type="button" className="bg-[#212121] text-white rounded-lg px-4">{t("Forward") || "Forward"}</button>
                            </div>
                        </div>
                    </ShowUI>
                    <ShowUI
                        ui_key={LEGISLATIVE_DRAFTING_UI_RULES.coordinator_forward_to_prosecutor.value}
                        currentStatus={formState.current_status}
                        forwardStatus={formState.forward_status}
                    >
                        <div className="flex ml-auto gap-4">
                            <div className="flex gap-4">
                                <button onClick={() => handleForward(
                                    formState.current_status === LEGISLATIVE_DRAFTING_REQUEST_STATUS.back_to_director.value ? LEGISLATIVE_DRAFTING_REQUEST_STATUS.forwarded_to_coordinator.value :
                                        LEGISLATIVE_DRAFTING_REQUEST_STATUS.assigned_to_prosecutor.value)} type="button" className="bg-[#212121] text-white rounded-lg px-4">{t("Forward") || "Forward"}</button>
                            </div>
                        </div>
                    </ShowUI>
                    <ShowUI
                        ui_key={LEGISLATIVE_DRAFTING_UI_RULES.coordinator_forward_to_head.value}
                        currentStatus={formState.current_status}
                        forwardStatus={formState.forward_status}
                    >
                        <div className="flex ml-auto gap-4">
                            <div className="flex gap-4">
                                <button onClick={() => handleForward(LEGISLATIVE_DRAFTING_REQUEST_STATUS.forwarded_to_head.value)}
                                    type="button" className="bg-[#212121] text-white rounded-lg px-4">{t("Forward") || "Forward"}</button>
                            </div>
                        </div>
                    </ShowUI>
                    <ShowUI
                        ui_key={LEGISLATIVE_DRAFTING_UI_RULES.additional_info_action.value}
                        currentStatus={formState.current_status}
                        forwardStatus={formState.forward_status}
                    >
                        <div className="flex ml-auto gap-4">
                            <div className="flex gap-4">
                                <button onClick={() => handleForward(LEGISLATIVE_DRAFTING_REQUEST_STATUS.not_forwarded.value)} type="button" className="bg-[#212121] text-white rounded-lg px-4">{t("Forward") || "Forward"}</button>
                            </div>
                        </div>
                    </ShowUI>
                    <ShowUI
                        ui_key={LEGISLATIVE_DRAFTING_UI_RULES.prosecutor_forward_to_director.value}
                        currentStatus={formState.current_status}
                        forwardStatus={formState.forward_status}
                    >
                        <div className="flex ml-auto gap-4">
                            <div className="flex gap-4">
                                <button onClick={() => handleForward(LEGISLATIVE_DRAFTING_REQUEST_STATUS.forwarded_to_director.value)}
                                    type="button" className="bg-[#212121] text-white rounded-lg px-4">{t("Forward") || "Forward"}</button>
                            </div>
                        </div>
                    </ShowUI>
                    <ShowUI
                        ui_key={LEGISLATIVE_DRAFTING_UI_RULES.prosecutor_forward_to_coordinator.value}
                        currentStatus={formState.current_status}
                        forwardStatus={formState.forward_status}
                    >
                        <div className="flex ml-auto gap-4">
                            <div className="flex gap-4">
                                <button onClick={() => handleForward(LEGISLATIVE_DRAFTING_REQUEST_STATUS.forwarded_to_coordinator.value)}
                                    type="button" className="bg-[#212121] text-white rounded-lg px-4">{t("Forward") || "Forward"}</button>
                            </div>
                        </div>
                    </ShowUI>
                    <ShowUI
                        ui_key={LEGISLATIVE_DRAFTING_UI_RULES.edit_prosecutor_forward_to_coordinator.value}
                        currentStatus={formState.current_status}
                        forwardStatus={formState.forward_status}
                    >
                        <div className="flex ml-auto gap-4">
                            <div className="flex gap-4">
                                <button onClick={() => handleForward(LEGISLATIVE_DRAFTING_REQUEST_STATUS.forwarded_to_coordinator.value)}
                                    type="button" className="bg-[#212121] text-white rounded-lg px-4">{t("Forward") || "Forward"}</button>
                            </div>
                        </div>
                    </ShowUI>
                    <ShowUI
                        ui_key={LEGISLATIVE_DRAFTING_UI_RULES.head_forward_to_prosecutor.value}
                        currentStatus={formState.current_status}
                        forwardStatus={formState.forward_status}
                    >
                        <div className="flex ml-auto gap-4">
                            <div className="flex gap-4">
                                <button onClick={() => handleForward(LEGISLATIVE_DRAFTING_REQUEST_STATUS.assigned_to_prosecutor.value)} type="button" className="bg-[#212121] text-white rounded-lg px-4">{t("Forward") || "Forward"}</button>
                            </div>
                        </div>
                    </ShowUI>
                    <div>
                    </div>
                </div>
                
                {/* Broadcast Card */}
                {contextData?.user?.roles?.includes("LRDIFUDDirector") &&
                    ((formState.current_status === "cafffee_aproval" && formState.forward_status === "forwarded_to_director") ||
                        (formState.current_status === "Published" && formState.forward_status === "not_forwarded")) && (
                        <div className="mb-4">
                            <BroadcastCard
                                onClick={handleBroadcast}
                                isPublished={formState.is_published}
                                title={t("BroadcastLegislativeDraftToPublic") || "Broadcast Legislative Draft to Public"}
                                description={t("BroadcastLegislativeDraftDescription") || "Share your completed legislative draft with the public to increase transparency and accessibility. Broadcasting makes the draft visible to all users and stakeholders."}
                                additionalInfo={t("BroadcastLegislativeDraftAdditionalInfo") || "Once broadcasted, the draft will be publicly accessible and can be viewed by anyone. You can unpublish it later if needed."}
                                loading={broadcastLoading}
                            />
                        </div>
                    )}

                {/* Remarks */}
                <ShowUI
                    ui_key={LEGISLATIVE_DRAFTING_UI_RULES.edit_head_forward_to_director.value}
                    currentStatus={formState.current_status}
                    forwardStatus={formState.forward_status}
                >
                    <HeadRemark setEdit={setEdit} setFormState={setFormState} formState={formState} />
                </ShowUI>
                <ShowUI
                    ui_key={LEGISLATIVE_DRAFTING_UI_RULES.edit_director_forward_to_coordinator.value}
                    currentStatus={formState.current_status}
                    forwardStatus={formState.forward_status}
                >
                    <DirectorRemark setEdit={setEdit} setFormState={setFormState} formState={formState} />
                </ShowUI>
                <ShowUI
                    ui_key={LEGISLATIVE_DRAFTING_UI_RULES.edit_coordinator_forward_to_prosecutor.value}
                    currentStatus={formState.current_status}
                    forwardStatus={formState.forward_status}
                >
                    <CoordinatorRemark setEdit={setEdit} setFormState={setFormState} formState={formState} />
                </ShowUI>
                
                {/* Draft Law Not Essential */}
                <ShowUI
                    ui_key={LEGISLATIVE_DRAFTING_UI_RULES.draft_non_essential.value}
                    currentStatus={formState.current_status}
                    forwardStatus={"all"}
                >
                    <div className="flex-1 bg-white mb-4 p-5 rounded-xl border border-gray-200 shadow-sm">
                        <div className="pb-2 border-b border-gray-200 mb-4">
                            <p className="font-semibold text-gray-800 text-base">{t("DraftLawNotEssential") || "Draft Law Not Essential"}</p>
                        </div>
                        <p className="text-[#747171] font-normal text-xs mb-3">{formatDay(formState?.created_at).rest}</p>
                        <p className="font-regular text-base text-[#606060]">{formState?.summary_non_essential_of_drafting}</p>
                        <ShowUI
                            ui_key={LEGISLATIVE_DRAFTING_UI_RULES.draft_non_essential.value}
                            currentStatus={formState.current_status}
                            forwardStatus={formState.forward_status}
                        >
                            <div
                                onClick={() => handleForward(LEGISLATIVE_DRAFTING_REQUEST_STATUS.forwarded_to_registrar.value)}
                                className="flex-1 text-center cursor-pointer mt-4 px-6 py-3 rounded-md bg-[#212121] text-white font-semibold hover:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-600"
                            >
                                {loading ? (
                                    <div className="flex items-center justify-center">
                                        {LoadingIcon} {t("Submitting") || "Submitting..."}
                                    </div>
                                ) : (
                                    t("Approve") || "Approve"
                                )}
                            </div>
                        </ShowUI>
                    </div>
                </ShowUI>
                
                {/* Prosecutor draft actions — standalone toolbar above the card */}
                {formState.current_status !== LEGISLATIVE_DRAFTING_REQUEST_STATUS.public_and_amended_file_submitted.value &&
                    <ShowUI
                        ui_key={LEGISLATIVE_DRAFTING_UI_RULES.draft_and_additional_file.value}
                        currentStatus={"all"}
                        forwardStatus={formState.forward_status}
                    >
                        <ProsecutorRemark setFormState={setFormState} formState={formState} />
                    </ShowUI>}

                {/* Draft Law Detail */}
                <div className="flex-1 bg-white mb-4 p-5 rounded-xl border border-gray-200 shadow-sm">
                    <div className="pb-2 border-b border-gray-200 mb-4 flex justify-between items-center">
                        <p className="font-semibold text-gray-800 text-base">{t("DraftLawDetail") || "Draft Law Detail"}</p>
                        {(contextData?.user?.roles?.includes("Registrar") ||
                            (contextData?.user?.user_type === "internal" && contextData?.user?.roles?.some((r: string) => r.includes("Registrar")))) ? (
                            !editDraftDetail ? (
                                <svg onClick={() => setEditDraftDetail(true)} className="hover:bg-gray-300 rounded-lg cursor-pointer" width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M3.66406 21V16.75L16.8641 3.575C17.0641 3.39167 17.2851 3.25 17.5271 3.15C17.7691 3.05 18.0231 3 18.2891 3C18.5551 3 18.8134 3.05 19.0641 3.15C19.3147 3.25 19.5314 3.4 19.7141 3.6L21.0891 5C21.2891 5.18333 21.4351 5.4 21.5271 5.65C21.6191 5.9 21.6647 6.15 21.6641 6.4C21.6641 6.66667 21.6184 6.921 21.5271 7.163C21.4357 7.405 21.2897 7.62567 21.0891 7.825L7.91406 21H3.66406ZM18.2641 7.8L19.6641 6.4L18.2641 5L16.8641 6.4L18.2641 7.8Z" fill="black" />
                                </svg>
                            ) : (
                                <p onClick={() => { setEditDraftDetail(false); setNewDraftFile(null); }} className="hover:bg-gray-300 rounded-lg px-4 cursor-pointer">{t("Cancel") || "Cancel"}</p>
                            )
                        ) : (
                            canReviewDraft && (
                            <>
                                {!editDraftDetail ? (
                                    <svg onClick={() => setEditDraftDetail(true)} className="hover:bg-gray-300 rounded-lg cursor-pointer" width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M3.66406 21V16.75L16.8641 3.575C17.0641 3.39167 17.2851 3.25 17.5271 3.15C17.7691 3.05 18.0231 3 18.2891 3C18.5551 3 18.8134 3.05 19.0641 3.15C19.3147 3.25 19.5314 3.4 19.7141 3.6L21.0891 5C21.2891 5.18333 21.4351 5.4 21.5271 5.65C21.6191 5.9 21.6647 6.15 21.6641 6.4C21.6641 6.66667 21.6184 6.921 21.5271 7.163C21.4357 7.405 21.2897 7.62567 21.0891 7.825L7.91406 21H3.66406ZM18.2641 7.8L19.6641 6.4L18.2641 5L16.8641 6.4L18.2641 7.8Z" fill="black" />
                                    </svg>
                                ) : (
                                    <p onClick={() => { setEditDraftDetail(false); setNewDraftFile(null); }} className="hover:bg-gray-300 rounded-lg px-4 cursor-pointer">{t("Cancel") || "Cancel"}</p>
                                )}
                            </>
                            )
                        )}
                    </div>
                    <p className="text-[#747171] font-normal text-xs mb-3">{formatDay(formState?.created_at).rest}</p>
                    {!editDraftDetail ? (
                        <p className="font-regular text-base text-[#606060]">{formState?.draft_summary}</p>
                    ) : (
                        <>
                            <div className="mt-4">
                                <label className="text-gray-500 font-normal text-sm">
                                    {t("LetterReferenceNumber") || "Letter Reference Number"}
                                </label>
                                <input
                                    type="text"
                                    name="request_reference_number"
                                    className="w-full mt-2 p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent"
                                    placeholder={t("EnterReferenceNumber") || "Enter reference number..."}
                                    value={formState?.request_reference_number || ""}
                                    onChange={handleChange}
                                />
                            </div>
                            <div className="mt-4">
                                <label className="text-gray-500 font-normal text-sm">
                                    {t("DraftSummary") || "Draft Summary"}
                                </label>
                                <textarea
                                    id="draft_summary"
                                    name="draft_summary"
                                    className="w-full mt-2 p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent resize-y min-h-[100px]"
                                    placeholder={t("EnterDraftSummary") || "Draft summary..."}
                                    value={formState?.draft_summary || ""}
                                    onChange={handleChange}
                                ></textarea>
                            </div>
                            <div className="mt-4">
                                <label className="text-gray-500 font-normal text-sm">
                                    {t("ReuploadDraftFileOptional") || "Re-upload Draft File (optional)"}
                                </label>
                                <input
                                    type="file"
                                    className="w-full mt-2 p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-800"
                                    onChange={(e) => {
                                        const file = e.target.files?.[0];
                                        if (file) {
                                            setNewDraftFile(file);
                                        }
                                    }}
                                />
                                {newDraftFile && (
                                    <p className="text-sm text-green-600 mt-1">{t("Selected") || "Selected"}: {newDraftFile.name}</p>
                                )}
                            </div>
                            <div className="mt-4">
                                <button
                                    onClick={handleSaveDraftDetail}
                                    disabled={draftDetailLoading}
                                    className="w-full px-6 py-3 rounded-md bg-[#212121] text-white font-semibold hover:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-600 disabled:opacity-50"
                                >
                                    {draftDetailLoading ? (
                                        <div className="flex items-center justify-center">
                                            {LoadingIcon} {t("Saving") || "Saving..."}
                                        </div>
                                    ) : (
                                        t("SaveChanges") || "Save Changes"
                                    )}
                                </button>
                            </div>
                        </>
                    )}

                    {/* Latest Draft File — shown inside the Draft Law Detail card */}
                    {latestDraft && (
                        (contextData?.user?.user_type !== "external" || !formState?.uploaded_files?.find((document: any) => document.document_type === "amended_law")) && (
                            <div className="mt-4 pt-4 border-t border-gray-200">
                                <p className="text-[#432101AB] font-normal text-base mb-2">{t("LatestDraftFile") || "Latest Draft File"}</p>
                                <DocumentViewerModal
                                    documents={[latestDraft]}
                                    baseUrl={`${process.env.REACT_APP_API_BASE_URL}legal_research/legal-research-request-documents/document/`}
                                />
                            </div>
                        )
                    )}

                    {/* Previous Draft Files Accordion — inside the Draft Law Detail card */}
                    {previousDrafts.length > 0 && contextData?.user?.user_type !== "external" && (
                        <div className="mt-4 border border-gray-200 rounded-lg">
                            <div
                                onClick={() => setShowPreviousDrafts(!showPreviousDrafts)}
                                className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50"
                            >
                                <p className="text-[#432101AB] font-normal text-base">{t("PreviousDraftFiles") || "Previous Draft Files"} ({previousDrafts.length})</p>
                                <svg
                                    className={`transform transition-transform ${showPreviousDrafts ? 'rotate-180' : ''}`}
                                    width="20"
                                    height="20"
                                    viewBox="0 0 20 20"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path d="M5 7.5L10 12.5L15 7.5" stroke="#432101AB" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </div>
                            {showPreviousDrafts && (
                                <div className="p-3 pt-0 border-t border-gray-200">
                                    {previousDrafts.map((draft: any, index: number) => (
                                        <div key={draft.uu_id || index} className="mt-3">
                                            <p className="text-[#747171] font-normal text-xs mb-1">{t("Version") || "Version"} {previousDrafts.length - index} - {formatDay(draft.created_at || draft.updated_at).rest}</p>
                                            <DocumentViewerModal
                                                documents={[draft]}
                                                baseUrl={`${process.env.REACT_APP_API_BASE_URL}legal_research/legal-research-request-documents/document/`}
                                            />
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                {/* Amended Law Files */}
                {formState?.uploaded_files?.find((document: any) => document.document_type === "amended_law") && (() => {
                    const amendedLawFiles = formState?.uploaded_files?.filter((document: any) => document.document_type === "amended_law") || [];
                    const sortedAmendedFiles = [...amendedLawFiles].sort((a: any, b: any) => new Date(b.created_at || b.updated_at).getTime() - new Date(a.created_at || a.updated_at).getTime());
                    const latestAmended = sortedAmendedFiles[0];
                    const previousAmendedFiles = sortedAmendedFiles.slice(1);

                    return (
                        <div className="mt-4 border-2 border-green-500 bg-green-50 rounded-lg p-4">
                            <p className="text-green-700 font-semibold text-base mb-2">{t("LatestAmendedLawFile") || "Latest Amended Law File"}</p>
                            <DocumentViewerModal
                                documents={[latestAmended]}
                                baseUrl={`${process.env.REACT_APP_API_BASE_URL}legal_research/legal-research-request-documents/document/`}
                            />
                            {previousAmendedFiles.length > 0 && contextData?.user?.user_type !== "external" && (
                                <div className="mt-4 border border-gray-200 rounded-lg">
                                    <div
                                        onClick={() => setShowPreviousAmended(!showPreviousAmended)}
                                        className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50"
                                    >
                                        <p className="text-[#432101AB] font-normal text-base">{t("PreviousAmendedLawFiles") || "Previous Amended Law Files"} ({previousAmendedFiles.length})</p>
                                        <svg
                                            className={`transform transition-transform ${showPreviousAmended ? 'rotate-180' : ''}`}
                                            width="20"
                                            height="20"
                                            viewBox="0 0 20 20"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path d="M5 7.5L10 12.5L15 7.5" stroke="#432101AB" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </div>
                                    {showPreviousAmended && (
                                        <div className="p-3 pt-0 border-t border-gray-200">
                                            {previousAmendedFiles.map((file: any, index: number) => (
                                                <div key={file.uu_id || index} className="mt-3">
                                                    <p className="text-[#747171] font-normal text-xs mb-1">{t("Version") || "Version"} {previousAmendedFiles.length - index} - {formatDay(file.created_at || file.updated_at).rest}</p>
                                                    <DocumentViewerModal
                                                        documents={[file]}
                                                        baseUrl={`${process.env.REACT_APP_API_BASE_URL}legal_research/legal-research-request-documents/document/`}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })()}

                {/* Public Feedback Files */}
                {formState?.uploaded_files?.find((document: any) => document.document_type === "public_feedback") && contextData?.user?.user_type !== "external" && (() => {
                    const publicFeedbackFiles = formState?.uploaded_files?.filter((document: any) => document.document_type === "public_feedback") || [];
                    const sortedFeedbackFiles = [...publicFeedbackFiles].sort((a: any, b: any) => new Date(b.created_at || b.updated_at).getTime() - new Date(a.created_at || a.updated_at).getTime());
                    const latestFeedback = sortedFeedbackFiles[0];
                    const previousFeedbackFiles = sortedFeedbackFiles.slice(1);

                    return (
                        <div className="mt-4">
                            <p className="text-[#432101AB] font-normal text-base mb-2">{t("LatestPublicFeedbackFile") || "Latest Public Feedback File"}</p>
                            <DocumentViewerModal
                                documents={[latestFeedback]}
                                baseUrl={`${process.env.REACT_APP_API_BASE_URL}legal_research/legal-research-request-documents/document/`}
                            />
                            {previousFeedbackFiles.length > 0 && (
                                <div className="mt-4 border border-gray-200 rounded-lg">
                                    <div
                                        onClick={() => setShowPreviousFeedback(!showPreviousFeedback)}
                                        className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50"
                                    >
                                        <p className="text-[#432101AB] font-normal text-base">{t("PreviousPublicFeedbackFiles") || "Previous Public Feedback Files"} ({previousFeedbackFiles.length})</p>
                                        <svg
                                            className={`transform transition-transform ${showPreviousFeedback ? 'rotate-180' : ''}`}
                                            width="20"
                                            height="20"
                                            viewBox="0 0 20 20"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path d="M5 7.5L10 12.5L15 7.5" stroke="#432101AB" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </div>
                                    {showPreviousFeedback && (
                                        <div className="p-3 pt-0 border-t border-gray-200">
                                            {previousFeedbackFiles.map((file: any, index: number) => (
                                                <div key={file.uu_id || index} className="mt-3">
                                                    <p className="text-[#747171] font-normal text-xs mb-1">{t("Version") || "Version"} {previousFeedbackFiles.length - index} - {formatDay(file.created_at || file.updated_at).rest}</p>
                                                    <DocumentViewerModal
                                                        documents={[file]}
                                                        baseUrl={`${process.env.REACT_APP_API_BASE_URL}legal_research/legal-research-request-documents/document/`}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })()}

                {/* Forward to Head — director or coordinator (inside Draft Law Detail) */}
                {!auditDraft &&
                    <ShowUI
                        ui_key={LEGISLATIVE_DRAFTING_UI_RULES.edit_audit_draft.value}
                        currentStatus={formState.current_status}
                        forwardStatus={formState.forward_status}
                    >
                        <div className="mt-4 pt-4 border-t border-gray-200">
                            <button
                                onClick={() => handleForward(LEGISLATIVE_DRAFTING_REQUEST_STATUS.forwarded_to_head.value)}
                                type="button"
                                disabled={loading}
                                className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-[#0A1D39] hover:bg-[#13294d] text-white font-semibold transition-colors disabled:opacity-60"
                            >
                                {loading ? (
                                    <>{LoadingIcon} {t("Submitting") || "Submitting..."}</>
                                ) : (
                                    <>
                                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                                        </svg>
                                        {t("ForwardToHead") || "Forward to Head"}
                                    </>
                                )}
                            </button>
                        </div>
                    </ShowUI>
                }
                </div>

                {/* Action Buttons */}
                <ShowUI
                    ui_key={LEGISLATIVE_DRAFTING_UI_RULES.public_and_amended.value}
                    currentStatus={formState.current_status}
                    forwardStatus={formState.forward_status}
                >
                    <div className="flex mb-4 gap-3">
                        <button onClick={() => setPublicFeedback(true)} className="flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-lg border border-[#0A1D39] text-[#0A1D39] hover:bg-[#0A1D39]/5 font-semibold transition-colors">
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m6-1.13a4 4 0 10-4-4 4 4 0 004 4z" />
                            </svg>
                            {t("SubmitPublicFeedback") || "Submit Public Feedback"}
                        </button>
                        <button onClick={() => setUploadAmend(true)} className="flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-[#0A1D39] hover:bg-[#13294d] text-white font-semibold transition-colors">
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                            </svg>
                            {t("UploadAmendedLegislativeFile") || "Upload Amended Legislative File"}
                        </button>
                    </div>
                </ShowUI>
                <ShowUI
                    ui_key={LEGISLATIVE_DRAFTING_UI_RULES.respond_amended.value}
                    currentStatus={formState.current_status}
                    forwardStatus={formState.forward_status}
                >
                    <button onClick={() => setUploadAmend(true)} className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-[#0A1D39] hover:bg-[#13294d] text-white font-semibold transition-colors">
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                        </svg>
                        {t("UploadAmendedLegislativeFile") || "Upload Amended Legislative File"}
                    </button>
                </ShowUI>
                
                {/* Legal Drafting Completion Letter */}
                {formState?.uploaded_files?.find((document: any) => document.document_type === "audit_summary_file") && contextData?.user?.user_type !== "external" &&
                    <div className="flex-1 bg-white mb-4 p-5 rounded-xl border border-gray-200 shadow-sm">
                        <div className="pb-2 border-b border-gray-200 mb-4">
                            <p>{t("LegalDraftingCompletionLetter") || "Legal Drafting Completion Letter"}</p>
                        </div>
                        <p className="text-[#747171] font-normal text-xs mb-3">{formatDay(formState?.created_at).rest}</p>
                        <p className="font-regular text-base text-[#606060]">{formState?.audit_summary}</p>
                        <DocumentViewerModal
                            documents={formState?.uploaded_files.filter((document: any) => document.document_type === "audit_summary_file")}
                            baseUrl={`${process.env.REACT_APP_API_BASE_URL}legal_research/legal-research-request-documents/document/`}
                        />
                    </div>
                }

                {/* Final Approval Section */}
                <ShowUI
                    ui_key={LEGISLATIVE_DRAFTING_UI_RULES.final_approval.value}
                    currentStatus={formState.current_status}
                    forwardStatus={formState.forward_status}
                >
                    <LegislativeRespondBackForm
                        request={formState}
                        setLegalResearch={setFormState}
                        isOpen={isRespondBackFormOpen}
                        onClose={() => setIsRespondBackFormOpen(false)}
                        forward={LEGISLATIVE_DRAFTING_REQUEST_STATUS.audit_responded.value}
                        feedbackType={"Audit"}
                    />
                    <div className="flex gap-4 mb-4">
                        <button onClick={handleRespondBackClick} className="p-4 flex-1 bg-[#D4D4D4] text-[#212121] rounded-lg">{t("RespondBack") || "Respond Back"}</button>
                        <button onClick={handleFinalApprove} className="p-4 flex-1 bg-[#212121] text-white rounded-lg">{t("FinalApproveDraftLaw") || "Final Approve Draft Law"}</button>
                    </div>
                </ShowUI>
                
                {auditDraft &&
                    <ShowUI
                        ui_key={LEGISLATIVE_DRAFTING_UI_RULES.edit_audit_draft.value}
                        currentStatus={formState.current_status}
                        forwardStatus={formState.forward_status}
                    >
                        <div>
                            <label className="text-gray-500 font-normal text-sm">
                                {t("AuditSummary") || "Audit Summary"}
                            </label>
                            <textarea
                                id="remark"
                                name="audit_summary"
                                className="w-full mt-2 p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent resize-y min-h-[100px]"
                                placeholder={t("DetailHere") || "detail here..."}
                                value={formState?.audit_summary}
                                onChange={handleChange}
                                required
                            ></textarea>
                            <div className="mb-4">
                                <DynamicFormInput
                                    inputs={[
                                        {
                                            type: "file",
                                            label: t("AuditDocument") || "Audit Document",
                                            name: "audit_summary_file",
                                            onChange: (file) => handleFileChange(file, "audit_summary_file"),
                                        },
                                    ]}
                                />
                            </div>
                            <div className="flex">
                                <button onClick={handleAudit} className="flex-1 border border-black p-4 rounded-lg bg-[#D4D4D4] text-[#212121] font-semibold text-base">{t("UploadAuditLegislativeDraft") || "Upload Audit Legislative Draft"}</button>
                            </div>
                        </div>
                    </ShowUI>
                }
                
                <div className="flex-1 overflow-y-scroll scrollbar-hide">
                    {/* Forward Draft Sections */}
                    <ShowUI
                        ui_key={LEGISLATIVE_DRAFTING_UI_RULES.forward_darft.value}
                        currentStatus={formState.current_status}
                        forwardStatus={formState.forward_status}
                    >
                        <LegislativeRespondBackForm
                            request={formState}
                            setLegalResearch={setFormState}
                            isOpen={isRespondBackFormOpen}
                            onClose={() => setIsRespondBackFormOpen(false)}
                            forward={LEGISLATIVE_DRAFTING_REQUEST_STATUS.draft_responded.value}
                            feedbackType={"Draft"}
                        />
                        <div className="mt-4 pb-8 border-b border-gray-200 flex flex-col sm:flex-row justify-between space-y-4 sm:space-y-0 sm:space-x-4">
                            <button
                                type="button"
                                className="flex-1 px-6 py-3 rounded-md bg-[#D4D4D4] text-[#212121] font-semibold hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-300"
                                onClick={handleRespondBackClick}
                            >
                                {loading ? (
                                    <div className="flex items-center justify-center">
                                        {LoadingIcon} {t("Submitting") || "Submitting..."}
                                    </div>
                                ) : (
                                    t("RespondBack") || "Respond back"
                                )}
                            </button>
                            <div
                                onClick={() => handleForward(LEGISLATIVE_DRAFTING_REQUEST_STATUS.forwarded_to_director.value)}
                                className="flex-1 text-center cursor-pointer px-6 py-3 rounded-md bg-[#212121] text-white font-semibold hover:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-600"
                            >
                                {loading ? (
                                    <div className="flex items-center justify-center">
                                        {LoadingIcon} {t("Submitting") || "Submitting..."}
                                    </div>
                                ) : (
                                    getApproveButtonText()
                                )}
                            </div>
                        </div>
                    </ShowUI>
                    
                    {canReviewDraft && (
                    <>
                        <LegislativeRespondBackForm
                            request={formState}
                            setLegalResearch={setFormState}
                            isOpen={isRespondBackFormOpen}
                            onClose={() => setIsRespondBackFormOpen(false)}
                            forward={LEGISLATIVE_DRAFTING_REQUEST_STATUS.draft_responded.value}
                            feedbackType={"Draft"}
                        />
                        <div className="mt-4 pb-8 border-b border-gray-200 flex flex-col sm:flex-row justify-between space-y-4 sm:space-y-0 sm:space-x-4">
                            <button
                                type="button"
                                className="flex-1 px-6 py-3 rounded-md bg-[#D4D4D4] text-[#212121] font-semibold hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-300"
                                onClick={handleRespondBackClick}
                            >
                                {loading ? (
                                    <div className="flex items-center justify-center">
                                        {LoadingIcon} {t("Submitting") || "Submitting..."}
                                    </div>
                                ) : (
                                    t("RespondBack") || "Respond back"
                                )}
                            </button>
                            <div
                                onClick={() => handleForward(
                                    // The coordinator sends the draft to the director; the director,
                                    // acting in the coordinator's place, sends it on to the head.
                                    formState.forward_status === LEGISLATIVE_DRAFTING_REQUEST_STATUS.forwarded_to_coordinator.value &&
                                        !isDirector
                                        ? LEGISLATIVE_DRAFTING_REQUEST_STATUS.forwarded_to_director.value
                                        : LEGISLATIVE_DRAFTING_REQUEST_STATUS.forwarded_to_head.value
                                )}
                                className="flex-1 text-center cursor-pointer px-6 py-3 rounded-md bg-[#212121] text-white font-semibold hover:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-600"
                            >
                                {loading ? (
                                    <div className="flex items-center justify-center">
                                        {LoadingIcon} {t("Submitting") || "Submitting..."}
                                    </div>
                                ) : (
                                    getApproveButtonText()
                                )}
                            </div>
                        </div>
                    </>
                    )}
                    
                    <ShowUI
                        ui_key={LEGISLATIVE_DRAFTING_UI_RULES.forward_darft_to_head.value}
                        currentStatus={formState.current_status}
                        forwardStatus={formState.forward_status}
                    >
                        <LegislativeRespondBackForm
                            request={formState}
                            setLegalResearch={setFormState}
                            isOpen={isRespondBackFormOpen}
                            onClose={() => setIsRespondBackFormOpen(false)}
                            forward={LEGISLATIVE_DRAFTING_REQUEST_STATUS.draft_responded.value}
                            feedbackType={"Draft"}
                        />
                        <div className="mt-4 pb-8 border-b border-gray-200 flex flex-col sm:flex-row justify-between gap-3">
                            <button
                                type="button"
                                disabled={loading}
                                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold transition-colors disabled:opacity-60"
                                onClick={handleRespondBackClick}
                            >
                                {loading ? (
                                    <>{LoadingIcon} {t("Submitting") || "Submitting..."}</>
                                ) : (
                                    <>
                                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                                        </svg>
                                        {t("RespondBack") || "Respond back"}
                                    </>
                                )}
                            </button>
                            <button
                                type="button"
                                disabled={loading}
                                onClick={handleDraftAllowFeedback}
                                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-lg border border-[#0A1D39] text-[#0A1D39] hover:bg-[#0A1D39]/5 font-semibold transition-colors disabled:opacity-60"
                            >
                                {loading ? (
                                    <>{LoadingIcon} {t("Submitting") || "Submitting..."}</>
                                ) : (
                                    <>
                                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 01-3.46 0" />
                                        </svg>
                                        {t("AllowPublicFeedback") || "Allow Public Feedback"}
                                    </>
                                )}
                            </button>
                            <button
                                type="button"
                                disabled={loading}
                                onClick={handleFinalApprove}
                                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-[#0A1D39] hover:bg-[#13294d] text-white font-semibold transition-colors disabled:opacity-60"
                            >
                                {loading ? (
                                    <>{LoadingIcon} {t("Submitting") || "Submitting..."}</>
                                ) : (
                                    <>
                                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                        </svg>
                                        {t("ApproveDraftLaw") || "Approve Draft Law"}
                                    </>
                                )}
                            </button>
                        </div>
                    </ShowUI>
                    
                    {/* Additional Information Section — latest request/response only */}
                    {!!latestAdditionalInfo &&
                        <div className="flex-1 bg-white mb-4 p-5 rounded-xl border border-gray-200 shadow-sm">
                            <div className="pb-2 border-b border-gray-200 mb-4">
                                <p className="font-semibold text-gray-800 text-base">{t("AdditionalInformationRequest") || "Additional Information Request"}</p>
                            </div>
                            <p className="text-[#747171] font-normal text-xs mb-3">{formatDay(latestAdditionalInfo.requested_at).rest}</p>
                            <p className="font-regular text-base text-[#606060]">{latestAdditionalInfo.request_summary}</p>
                            {additionalInfoDocuments && additionalInfoDocuments.length > 0 && (
                                <DocumentViewerModal
                                    documents={additionalInfoDocuments}
                                    baseUrl={ADDITIONAL_INFO_BASE_URL}
                                />
                            )}
                            {latestAdditionalInfo?.response_note && (
                                <div className="mt-4 pt-4 border-t border-gray-200">
                                    <p className="text-[#432101AB] font-normal text-base mb-2">{t("ResponseNote") || "Response Note"}</p>
                                    <p className="text-[#747171] font-normal text-xs mb-1">{latestAdditionalInfo?.responded_at ? formatDay(latestAdditionalInfo.responded_at).rest : ''}</p>
                                    <p className="font-regular text-base text-[#606060]">{latestAdditionalInfo?.response_note}</p>
                                    {latestAdditionalInfo?.response_file && (
                                        <div className="mt-4">
                                            <FileView title={t("ResponseFile") || "Response File"} file={latestAdditionalInfo?.response_file} />
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    }
                    
                    {/* Documents Section */}
                    <div className="mt-6 space-y-4 bg-white border border-gray-200 shadow-sm p-5 mb-8 rounded-xl">
                        <p className="text-[#432101AB] font-normal text-base">{t("RegisterNumber") || "Register Number"}: {formState.request_reference_number}</p>
                        
                        {/* Latest Draft Law File */}
                        {formState?.uploaded_files?.find((document: any) => document.document_type === "law_draft") && (() => {
                            const lawDraftFiles = formState?.uploaded_files?.filter((document: any) => document.document_type === "law_draft") || [];
                            const sortedDraftFiles = [...lawDraftFiles].sort((a: any, b: any) => new Date(b.created_at || b.updated_at).getTime() - new Date(a.created_at || a.updated_at).getTime());
                            const latestDraftDoc = sortedDraftFiles[0];

                            return (
                                <div className="mt-4">
                                    <p className="text-[#432101AB] font-normal text-base mb-2">{t("LatestDraftLawFile") || "Latest Draft Law File"}</p>
                                    <DocumentViewerModal
                                        documents={[latestDraftDoc]}
                                        baseUrl={`${process.env.REACT_APP_API_BASE_URL}legal_research/legal-research-request-documents/document/`}
                                    />
                                </div>
                            );
                        })()}
                        
                        {formState?.uploaded_files?.find((document: any) => document.document_type === "instruction") &&
                            <DocumentViewerModal
                                documents={formState?.uploaded_files.filter((document: any) => document.document_type === "instruction")}
                                baseUrl={`${process.env.REACT_APP_API_BASE_URL}legal_research/legal-research-request-documents/document/`}
                            />
                        }
                        {formState?.uploaded_files?.find((document: any) => document.document_type === "request_letter") &&
                            <DocumentViewerModal
                                documents={formState?.uploaded_files.filter((document: any) => document.document_type === "request_letter")}
                                baseUrl={`${process.env.REACT_APP_API_BASE_URL}legal_research/legal-research-request-documents/document/`}
                            />
                        }
                        {formState?.uploaded_files?.find((document: any) => document.document_type === "research_completion_letter") && (contextData?.user?.user_type === "external" || contextData?.user?.roles?.includes("Registrar")) &&
                            <DocumentViewerModal
                                documents={formState?.uploaded_files.filter((document: any) => document.document_type === "research_completion_letter")}
                                baseUrl={`${process.env.REACT_APP_API_BASE_URL}legal_research/legal-research-request-documents/document/`}
                            />
                        }
                        {formState?.uploaded_files?.find((document: any) => document.document_type === "amended_draft") &&
                            <DocumentViewerModal
                                documents={formState?.uploaded_files.filter((document: any) => document.document_type === "amended_draft")}
                                baseUrl={`${process.env.REACT_APP_API_BASE_URL}legal_research/legal-research-request-documents/document/`}
                            />
                        }
                        <div className="mb-8">
                            <p className="text-[#432101AB] font-normal text-base">{t("Summary") || "Summary"}</p>
                            <p className="text-gray-500 font-medium mb-2">
                                {formState?.draft_instruction_summary}
                            </p>
                        </div>
                    </div>
                    
                    {/* Team List */}
                    {formState?.prosecutor_assignments &&
                        formState?.prosecutor_assignments.length !== 0 &&
                        !(contextData?.user?.user_type === "external") &&
                        (<div className="mt-6 mb-6 flex bg-gray-50 rounded-lg border border-gray-200">
                            <ActionAvatars
                                title={t("TeamList") || "Team List"}
                                users={formState?.prosecutor_assignments}
                                prosecutor={true}
                            />
                            {contextData?.user?.roles?.some((p) => p === "LegalResearchCoordinator" || p === "LRDIFUDDirector") && (
                                <svg onClick={() => setIsEditTeamModalOpen(true)} className="my-auto mr-2 hover:bg-gray-300 rounded-lg cursor-pointer" width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <title>{t("EditTeam") || "Edit Team"}</title>
                                    <path d="M3.66406 21V16.75L16.8641 3.575C17.0641 3.39167 17.2851 3.25 17.5271 3.15C17.7691 3.05 18.0231 3 18.2891 3C18.5551 3 18.8134 3.05 19.0641 3.15C19.3147 3.25 19.5314 3.4 19.7141 3.6L21.0891 5C21.2891 5.18333 21.4351 5.4 21.5271 5.65C21.6191 5.9 21.6647 6.15 21.6641 6.4C21.6641 6.66667 21.6184 6.921 21.5271 7.163C21.4357 7.405 21.2897 7.62567 21.0891 7.825L7.91406 21H3.66406ZM18.2641 7.8L19.6641 6.4L18.2641 5L16.8641 6.4L18.2641 7.8Z" fill="black" />
                                </svg>)}
                            <svg onClick={() => setViewTeam(!viewTeam)} className="mr-2 ml-auto my-auto cursor-pointer" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <mask id="mask0_9_31087" maskUnits="userSpaceOnUse" x="1" y="5" width="22" height="14">
                                    <path d="M12 18C17.523 18 22 12 22 12C22 12 17.523 6 12 6C6.477 6 2 12 2 12C2 12 6.477 18 12 18Z" fill="white" stroke="white" stroke-width="1.33333" stroke-linejoin="round" />
                                    <path d="M12 14.5C12.663 14.5 13.2989 14.2366 13.7678 13.7678C14.2366 13.2989 14.5 12.663 14.5 12C14.5 11.337 14.2366 10.7011 13.7678 10.2322C13.2989 9.76339 12.663 9.5 12 9.5C11.337 9.5 10.7011 9.76339 10.2322 10.2322C9.76339 10.7011 9.5 11.337 9.5 12C9.5 12.663 9.76339 13.2989 10.2322 13.7678C10.7011 14.2366 11.337 14.5 12 14.5Z" fill="black" stroke="black" stroke-width="1.33333" stroke-linejoin="round" />
                                </mask>
                                <g mask="url(#mask0_9_31087)">
                                    <path d="M0 0H24V24H0V0Z" fill="#EFC83D" />
                                </g>
                            </svg>
                        </div>)}
                    
                    {edit &&
                        <ShowUI
                            ui_key={LEGISLATIVE_DRAFTING_UI_RULES.edit_coordinator_forward_to_prosecutor.value}
                            currentStatus={"all"}
                            forwardStatus={"all"}
                        >
                            <CoordinatorRemark setEdit={setEdit} setFormState={setFormState} formState={formState} />
                        </ShowUI>
                    }
                    <br />
                    
                    {formState?.prosecutor_assignments_info &&
                        formState?.prosecutor_assignments_info.length !== 0 &&
                        (<div className="mt-6 mb-6">
                            <ActionAvatars
                                title={t("TeamList") || "Team List"}
                                users={formState?.prosecutor_assignments_info}
                                prosecutor={true}
                            />
                        </div>)}
                    
                    {/* Additional Details for External Stakeholders */}
                    {formState.requested_by !== "internal" &&
                        contextData?.user?.user_type === "internal" &&
                        <>
                            <div>
                                <div className="flex">
                                    <p className="text-[#432101AB] font-normal text-base">{t("Stakeholder") || "Stakeholder"}</p>
                                    <p className="ml-auto text-[#0A1D39] font-semibold text-base">{formState?.organization_name}</p>
                                </div>
                                <hr className="w-full border-t-2 border-gray-300 my-4" />
                                <div className="flex">
                                    <p className="text-[#432101AB] font-normal text-base">{t("StakeholderType") || "Stakeholder Type"}</p>
                                    <p className="ml-auto text-[#0A1D39] font-semibold text-base">{formState?.organization_type}</p>
                                </div>
                                <hr className="w-full border-t-2 border-gray-300 my-4" />
                                <div className="flex">
                                    <p className="text-[#432101AB] font-normal text-base">{t("AppliedDate") || "Applied Date"}</p>
                                    <p className="ml-auto text-[#0A1D39] font-semibold text-base">{formatDate(formState?.created_at)}</p>
                                </div>
                                <hr className="w-full border-t-2 border-gray-300 my-4" />
                                <div className="flex">
                                    <p className="text-[#432101AB] font-normal text-base">{t("Address") || "Address"}</p>
                                    <p className="ml-auto text-[#0A1D39] font-semibold text-base">{formState?.organization_state}, {formState?.organization_zone}, {formState?.organization_kebele}</p>
                                </div>
                                <hr className="w-full border-t-2 border-gray-300 my-4" />
                                <div className="flex">
                                    <p className="text-[#432101AB] font-normal text-base">{t("Email") || "Email"}</p>
                                    <p className="ml-auto text-[#0A1D39] font-semibold text-base">{formState?.email_user_organization}</p>
                                </div>
                                <hr className="w-full border-t-2 border-gray-300 my-4" />
                            </div>
                            <br />
                        </>}

                    {/* Remark Section */}
                    <ShowUI
                        ui_key={LEGISLATIVE_DRAFTING_UI_RULES.internal_ui.value}
                        currentStatus={"all"}
                        forwardStatus={"all"}
                    >
                        {formState.remarks && formState?.remarks?.find((remark: any) => remark.role === "head") &&
                            <div>
                                <p className="text-[#432101AB] font-normal text-base">{t("RemarkForDirector") || "Remark For Director"}</p>
                                <p className="text-gray-500 font-medium mb-2">
                                    {formState?.remarks.filter((remark: any) => remark.role === "head").slice().reverse()[0]?.remark}
                                </p>
                                <br />
                            </div>
                        }
                        {formState.remarks && formState?.remarks?.find((remark: any) => remark.role === "director") &&
                            <div>
                                <p className="text-[#432101AB] font-normal text-base">{t("RemarkForCoordinator") || "Remark For Coordinator"}</p>
                                <p className="text-gray-500 font-medium mb-2">
                                    {formState?.remarks.filter((remark: any) => remark.role === "director").slice().reverse()[0]?.remark}
                                </p>
                                <br />
                            </div>
                        }
                        {formState.remarks && formState?.remarks?.find((remark: any) => remark.role === "coordinator") &&
                            <div>
                                <p className="text-[#432101AB] font-normal text-base">{t("RemarkForProsecutor") || "Remark For Prosecutor"}</p>
                                <p className="text-gray-500 font-medium mb-2">
                                    {formState?.remarks.filter((remark: any) => remark.role === "coordinator").slice().reverse()[0]?.remark || t("NoComment") || "No Comment"}
                                </p>
                                <br />
                            </div>
                        }
                    </ShowUI>
                </div>
                
            </div>
            
            {/* Modals */}
            {publicFeedback &&
                (<PublicFeedback
                    data={formState}
                    setLegalResearch={setFormState}
                    isOpen={publicFeedback}
                    onClose={() => setPublicFeedback(false)}
                />)
            }
            {uploadAmend &&
                (<UploadAmendedFile
                    data={formState}
                    setLegalResearch={setFormState}
                    isOpen={uploadAmend}
                    onClose={() => setUploadAmend(false)}
                />)
            }
            {viewTeam &&
                <TeamView teamMembers={formState.prosecutor_assignments} onClose={() => setViewTeam(false)} />}
            <EditTeamModal
                request={formState}
                isOpen={isEditTeamModalOpen}
                onClose={() => setIsEditTeamModalOpen(false)}
                setFormState={setFormState}
            />
            {showConfirmPublish && (
                <ConfirmationModal1
                    isOpen={showConfirmPublish}
                    isLoading={loading}
                    onCancel={() => setShowConfirmPublish(false)}
                    onConfirm={handlePublish}
                    title={t("ConfirmBroadcast") || "Confirm Broadcast"}
                    message={t("ConfirmBroadcastMessage") || "Are you sure you want to broadcast this legislative draft to the public website? This action cannot be undone."}
                    confirmText={t("Broadcast") || "Broadcast"}
                    cancelText={t("Cancel") || "Cancel"}
                    type="info"
                />
            )}
        </div>
    );
}

export default LegislativeDraftDetail;